import { useState } from 'react';
import {
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MapPinIcon,
  XMarkIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

/**
 * Panel to display addresses that failed to geocode.
 * Shows the address, voter name (if available), and reason for failure.
 */
export default function FailedGeocodesPanel({
  failedGeocodes = [],
  totalFailed = 0,
  onRetryGeocode,
  isRetrying = false
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState(new Set());

  if (!failedGeocodes || failedGeocodes.length === 0) {
    return null;
  }

  const displayCount = failedGeocodes.length;
  const hasMore = totalFailed > displayCount;

  const toggleRecord = (voterId) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(voterId)) {
      newSelected.delete(voterId);
    } else {
      newSelected.add(voterId);
    }
    setSelectedRecords(newSelected);
  };

  const selectAll = () => {
    if (selectedRecords.size === failedGeocodes.length) {
      setSelectedRecords(new Set());
    } else {
      setSelectedRecords(new Set(failedGeocodes.map(r => r.voter_id)));
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg shadow overflow-hidden">
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-amber-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
          <span className="text-sm font-semibold text-amber-800">
            Failed Geocodes ({totalFailed})
          </span>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="h-4 w-4 text-amber-600" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 text-amber-600" />
        )}
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-amber-200">
          {/* Info text */}
          <div className="px-3 py-2 bg-amber-100/50 text-xs text-amber-700">
            These addresses could not be resolved. Check for typos or missing information.
          </div>

          {/* Actions bar */}
          {onRetryGeocode && (
            <div className="px-3 py-2 border-b border-amber-200 flex items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-xs text-amber-700">
                <input
                  type="checkbox"
                  checked={selectedRecords.size === failedGeocodes.length && failedGeocodes.length > 0}
                  onChange={selectAll}
                  className="h-3 w-3 text-amber-600 rounded border-amber-300"
                />
                Select all
              </label>
              <button
                onClick={() => onRetryGeocode(Array.from(selectedRecords))}
                disabled={selectedRecords.size === 0 || isRetrying}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 hover:text-amber-800 hover:bg-amber-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRetrying ? (
                  <ArrowPathIcon className="h-3 w-3 animate-spin" />
                ) : (
                  <ArrowPathIcon className="h-3 w-3" />
                )}
                Retry Selected
              </button>
            </div>
          )}

          {/* Records list */}
          <div className="max-h-64 overflow-y-auto">
            {failedGeocodes.map((record, idx) => (
              <div
                key={record.voter_id || idx}
                className="flex items-start gap-2 p-2 border-b border-amber-100 last:border-b-0 hover:bg-amber-100/50"
              >
                {onRetryGeocode && (
                  <input
                    type="checkbox"
                    checked={selectedRecords.has(record.voter_id)}
                    onChange={() => toggleRecord(record.voter_id)}
                    className="mt-1 h-3 w-3 text-amber-600 rounded border-amber-300"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <MapPinIcon className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      {record.name && (
                        <div className="text-xs font-medium text-gray-900 truncate">
                          {record.name}
                        </div>
                      )}
                      <div className="text-xs text-gray-700 break-words">
                        {record.address || 'No address'}
                      </div>
                      <div className="text-xs text-amber-600 mt-0.5 italic">
                        {record.reason || 'Geocoding failed'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Show more indicator */}
          {hasMore && (
            <div className="px-3 py-2 bg-amber-100/50 text-center">
              <span className="text-xs text-amber-600">
                Showing {displayCount} of {totalFailed} failed records
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
